/* eslint-disable complexity */
import { ErrorBanner } from '@core/components';
import { LabelWithScoreThresholdSelector, type ScoreLabelOption, type LabelWithScore } from '@core/components/LabelWithScoreThresholdSelector';
import { LabelWithSeveritySelector, type LabelOption, type LabelWithSeverity } from '@core/components/LabelWithSeveritySelector';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { TextInput, LoadingSpinner, Switch, Dialog } from '@soteria/design-system-ve';
import { ContentType } from '@system-config/label-management/domain';
import { useStandardLabels } from '@system-config/label-management/hooks';
import { PolicyScope, type StandardPolicy, type PolicyLabel } from '@system-config/policy-management/domain';
import { useCreateStandardPolicy, useUpdateStandardPolicy } from '@system-config/policy-management/hooks';
import { useEffect, useState, useMemo, useCallback } from 'react';

import * as styles from './StandardPolicyDialog.css.ts';

interface StandardPolicyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  policy?: StandardPolicy;
}

/**
 * Sanitizes labels by trimming labelId and removing Apollo's __typename
 */
function sanitizePolicyLabel(label: PolicyLabel): PolicyLabel {
  const { __typename, ...cleanLabel } = label as PolicyLabel & { __typename?: string };

  return {
    ...cleanLabel,
    labelId: cleanLabel.labelId.trim(),
  };
}

/**
 * Helper function to validate policy labels.
 * Text and Username labels use severity; Image labels use scoreThreshold.
 * Ensures mutual exclusivity between the two value types.
 */
function isValidPolicyLabel(label: PolicyLabel | null | undefined): label is PolicyLabel {
  if (!label || typeof label.labelId !== 'string' || label.labelId.trim() === '') {
    return false;
  }

  // Text or username label: must have severity >= 1
  const hasValidSeverity = typeof label.severity === 'number' && label.severity >= 1;

  // Image label: must have scoreThreshold >= 0
  const hasValidScoreThreshold = typeof label.scoreThreshold === 'number' && label.scoreThreshold >= 0;

  // Ensure mutual exclusivity between severity and scoreThreshold
  return (hasValidSeverity && !hasValidScoreThreshold) || (hasValidScoreThreshold && !hasValidSeverity);
}

/**
 * Normalize labels for stable comparison (order-independent)
 */
function normalizeLabels(labels: PolicyLabel[]): string {
  return JSON.stringify([...labels].map(sanitizePolicyLabel).sort((a, b) => a.labelId.localeCompare(b.labelId)));
}

/**
 * Check if form is valid for submission
 */
function checkFormValidity(formData: FormData, policy?: StandardPolicy): boolean {
  const requiredFieldsValid = formData.name.trim() !== '';

  if (policy) {
    const hasChanges =
      formData.name !== policy.name ||
      formData.description !== (policy.description || '') ||
      formData.isEnabled !== policy.isEnabled ||
      normalizeLabels(formData.labels) !== normalizeLabels(policy.labels);

    return requiredFieldsValid && hasChanges;
  }

  return requiredFieldsValid;
}

interface FormData {
  name: string;
  description: string;
  isEnabled: boolean;
  labels: PolicyLabel[];
}

function StandardPolicyDialogComponent({ isOpen, onClose, policy }: StandardPolicyDialogProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    isEnabled: true,
    labels: [],
  });

  const { labels: availableLabels, loading: labelsLoading } = useStandardLabels();

  // Separate text, image, and username label options from available labels
  const { textLabelOptions, imageLabelOptions, usernameLabelOptions } = useMemo(() => {
    const text: LabelOption[] = availableLabels
      .filter(label => label.contentType === ContentType.Text)
      .map(label => ({ id: label.id, labelName: label.labelName, maxSeverity: label.maxSeverity ?? undefined }));

    const image: ScoreLabelOption[] = availableLabels
      .filter(label => label.contentType === ContentType.Image)
      .map(label => ({ id: label.id, labelName: label.labelName }));

    const username: LabelOption[] = availableLabels
      .filter(label => label.contentType === ContentType.Username)
      .map(label => ({ id: label.id, labelName: label.labelName, maxSeverity: label.maxSeverity ?? undefined }));

    return { textLabelOptions: text, imageLabelOptions: image, usernameLabelOptions: username };
  }, [availableLabels]);

  // ID sets derived from available label options — used to classify severity-based labels
  const { textLabelIdSet, imageLabelIdSet, usernameLabelIdSet } = useMemo(
    () => ({
      textLabelIdSet: new Set(textLabelOptions.map(l => l.id)),
      imageLabelIdSet: new Set(imageLabelOptions.map(l => l.id)),
      usernameLabelIdSet: new Set(usernameLabelOptions.map(l => l.id)),
    }),
    [textLabelOptions, imageLabelOptions, usernameLabelOptions]
  );

  // Transform PolicyLabel[] to LabelWithSeverity[] (text labels only)
  const textLabelsWithSeverity: LabelWithSeverity[] = useMemo(
    () =>
      (formData.labels || [])
        .filter(label => textLabelIdSet.has(label.labelId) && typeof label.severity === 'number' && label.severity >= 1)
        .map(label => ({
          labelId: label.labelId,
          severity: label.severity ?? 0,
        })),
    [formData.labels, textLabelIdSet]
  );

  // Transform PolicyLabel[] to LabelWithScore[] (image labels only)
  const imageLabelsWithScore: LabelWithScore[] = useMemo(
    () =>
      (formData.labels || [])
        .filter(label => imageLabelIdSet.has(label.labelId) && typeof label.scoreThreshold === 'number' && label.scoreThreshold >= 0)
        .map(label => ({
          labelId: label.labelId,
          scoreThreshold: label.scoreThreshold ?? 0,
        })),
    [formData.labels, imageLabelIdSet]
  );

  // Transform PolicyLabel[] to LabelWithSeverity[] (username labels only)
  const usernameLabelsWithSeverity: LabelWithSeverity[] = useMemo(
    () =>
      (formData.labels || [])
        .filter(label => usernameLabelIdSet.has(label.labelId) && typeof label.severity === 'number' && label.severity >= 1)
        .map(label => ({
          labelId: label.labelId,
          severity: label.severity ?? 0,
        })),
    [formData.labels, usernameLabelIdSet]
  );

  // Handle text label changes — preserve image and username labels
  const handleTextLabelsChange = useCallback(
    (labels: LabelWithSeverity[]) => {
      const newLabels: PolicyLabel[] = labels.map(label => ({ labelId: label.labelId, severity: label.severity }));
      const otherLabels = formData.labels.filter(l => !textLabelIdSet.has(l.labelId));

      setFormData(prev => ({ ...prev, labels: [...otherLabels, ...newLabels] }));
    },
    [formData.labels, textLabelIdSet]
  );

  // Handle image label changes — preserve text and username labels
  const handleImageLabelsChange = useCallback(
    (labels: LabelWithScore[]) => {
      const newLabels: PolicyLabel[] = labels.map(label => ({ labelId: label.labelId, scoreThreshold: label.scoreThreshold }));
      const otherLabels = formData.labels.filter(l => !imageLabelIdSet.has(l.labelId));

      setFormData(prev => ({ ...prev, labels: [...otherLabels, ...newLabels] }));
    },
    [formData.labels, imageLabelIdSet]
  );

  // Handle username label changes — preserve text and image labels
  const handleUsernameLabelsChange = useCallback(
    (labels: LabelWithSeverity[]) => {
      const newLabels: PolicyLabel[] = labels.map(label => ({ labelId: label.labelId, severity: label.severity }));
      const otherLabels = formData.labels.filter(l => !usernameLabelIdSet.has(l.labelId));

      setFormData(prev => ({ ...prev, labels: [...otherLabels, ...newLabels] }));
    },
    [formData.labels, usernameLabelIdSet]
  );

  useEffect(() => {
    // Reset form data when dialog opens or policy changes
    if (isOpen) {
      setFormData({
        name: policy?.name || '',
        description: policy?.description || '',
        isEnabled: policy?.isEnabled ?? true,
        labels: policy?.labels || [],
      });
    }
  }, [isOpen, policy]);

  const { createStandardPolicy, loading: isCreating, errors: createErrors, resetErrors: resetCreateErrors } = useCreateStandardPolicy();
  const { updateStandardPolicy, loading: isUpdating, errors: updateErrors, resetErrors: resetUpdateErrors } = useUpdateStandardPolicy();

  const isLoading = isCreating || isUpdating;
  const errors = createErrors || updateErrors;
  const isFormValid = checkFormValidity(formData, policy);

  const handleInputChange = (field: keyof FormData) => (value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    resetCreateErrors();
    resetUpdateErrors();

    // Filter and sanitize labels
    const validLabels = formData.labels.filter(isValidPolicyLabel).map(sanitizePolicyLabel);
    const policyData = { ...formData, labels: validLabels, scope: PolicyScope.Global };

    const success = policy ? await updateStandardPolicy(policy.id, policyData) : await createStandardPolicy(policyData);

    if (success) {
      onClose();
    }
  };

  const handleDialogClose = () => {
    resetCreateErrors();
    resetUpdateErrors();
    onClose();
  };

  return (
    <Dialog
      cancelAction={{ label: 'Close' }}
      description={policy ? `Edit the ${policy.name} standard policy` : 'Create a new standard policy'}
      isOpen={isOpen}
      primaryAction={{
        label: isLoading ? <LoadingSpinner /> : policy ? 'Save Changes' : 'Add Policy',
        onClick: handleSubmit,
        disabled: isLoading || !isFormValid || labelsLoading,
      }}
      size="lg"
      title={policy ? `Edit ${policy.name}` : 'Add Standard Policy'}
      onClose={handleDialogClose}
    >
      <div className={styles.form}>
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error saving the policy:" />}

        {/* Policy Status Toggle - Only show when editing */}
        {policy && (
          <div className={styles.toggleContainer}>
            <span className={styles.toggleLabel}>Enabled</span>
            <Switch checked={formData.isEnabled} id="isEnabled" label="Toggle policy enabled status" onChange={handleInputChange('isEnabled')} />
          </div>
        )}

        {/* Policy Name Input */}
        <TextInput required id="name" label="Policy Name" placeholder="Enter policy name" value={formData.name} onChange={handleInputChange('name')} />

        {/* Policy Description Input */}
        <TextInput
          id="description"
          label="Description"
          placeholder="Enter policy description"
          value={formData.description}
          onChange={handleInputChange('description')}
        />

        {/* Text Labels Selector */}
        {textLabelOptions.length > 0 && (
          <LabelWithSeveritySelector
            showSeverityDescription
            availableLabels={textLabelOptions}
            defaultMaxSeverity={3}
            defaultSeverity={2}
            emptyStateMessage="No text labels selected. Add labels to define text content criteria."
            loading={labelsLoading}
            selectedLabels={textLabelsWithSeverity}
            title="Text Content Labels"
            onChange={handleTextLabelsChange}
          />
        )}

        {/* Image Labels Selector */}
        {imageLabelOptions.length > 0 && (
          <LabelWithScoreThresholdSelector
            showScoreDescription
            availableLabels={imageLabelOptions}
            defaultScoreThreshold={70}
            emptyStateMessage="No image labels selected. Add labels to define image content criteria."
            loading={labelsLoading}
            maxScore={100}
            minScore={0}
            selectedLabels={imageLabelsWithScore}
            title="Image Content Labels"
            onChange={handleImageLabelsChange}
          />
        )}

        {/* Username Labels Selector */}
        {usernameLabelOptions.length > 0 && (
          <LabelWithSeveritySelector
            showSeverityDescription
            availableLabels={usernameLabelOptions}
            defaultMaxSeverity={3}
            defaultSeverity={2}
            emptyStateMessage="No username labels selected. Add labels to define username content criteria."
            loading={labelsLoading}
            selectedLabels={usernameLabelsWithSeverity}
            title="Username Content Labels"
            onChange={handleUsernameLabelsChange}
          />
        )}
      </div>
    </Dialog>
  );
}

export const StandardPolicyDialog = withAppInsightsTracking(StandardPolicyDialogComponent, 'StandardPolicyDialog');
